import { Document, ProcessingStatus } from '@/types/Document';
import DocumentTypeDropdown from './DocumentTypeDropdown';
import ProcessingStatusBadge from './ProcessingStatusBadge';
import DocumentActions from './DocumentActions';

interface DocumentTableProps {
  documents: Document[];
  processingStatuses: Record<string, ProcessingStatus>;
  processingResults: Record<string, any>;
  processingDocId: string | null;
  docTypeSelections: Record<string, string>;
  onDocumentClick: (documentId: string) => void;
  onTypeChange: (documentId: string, typeId: string) => Promise<void>;
  onProcess: (documentId: string) => void;
  onDownload: (documentId: string, filename: string) => void;
  onDelete: (documentId: string) => void;
  onViewResults: (documentId: string) => void;
}

export default function DocumentTable({
  documents,
  processingStatuses,
  processingResults,
  processingDocId,
  docTypeSelections,
  onDocumentClick,
  onTypeChange,
  onProcess,
  onDownload,
  onDelete,
  onViewResults
}: DocumentTableProps) {
  if (documents.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No documents</h3>
        <p className="mt-1 text-sm text-gray-500">Upload your first document to get started.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-name">
              Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Size
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Pages
            </th>
            <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Upload Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {documents.map((doc) => (
            <tr 
              key={doc.id} 
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => onDocumentClick(doc.id)}
            >
              <td className="px-6 py-4 w-[50px]" onClick={(e) => e.stopPropagation()}>
                <DocumentTypeDropdown
                  documentId={doc.id}
                  selectedType={docTypeSelections[doc.id] || 'procedure'}
                  processingStatus={processingStatuses[doc.id]}
                  isProcessing={processingDocId === doc.id}
                  onTypeChange={onTypeChange}
                />
              </td>
              <td className="px-6 py-4 whitespace-nowrap max-w-name">
                <div className="flex items-center">
                  <div className="ml-0">
                    <div className="text-sm font-medium text-gray-900">{doc.originalName}</div>
                    {processingResults[doc.id] && processingStatuses[doc.id] === 'done' && (
                      <div 
                        className="text-xs text-blue-600 hover:underline mt-1 cursor-pointer flex items-center" 
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewResults(doc.id);
                        }}
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View Processing Results
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {Math.round(doc.size / 1024)} KB
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {doc.pageCount ? doc.pageCount : 'N/A'}
              </td>
              <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {new Date(doc.uploadDate).toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <ProcessingStatusBadge status={processingStatuses[doc.id]} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <DocumentActions
                  documentId={doc.id}
                  processingStatus={processingStatuses[doc.id]}
                  isProcessing={processingDocId === doc.id}
                  onProcess={onProcess}
                  onDownload={onDownload}
                  onDelete={onDelete}
                  originalName={doc.originalName}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
